import React, { useState, useEffect } from 'react';
import { useContent } from '../../contexts/ContentContext';
import ImageUpload from './ImageUpload';
import './FeaturedProgramsAdmin.css';

const FeaturedProgramsAdmin = ({ contentPath }) => {
  const { getContent, updateContent } = useContent();
  const [sectionData, setSectionData] = useState(null);

  useEffect(() => {
    const data = getContent(contentPath);
    setSectionData(data);
  }, [contentPath, getContent]);

  const handleSectionUpdate = (field, value) => {
    updateContent(`${contentPath}.${field}`, value);
    setSectionData(getContent(contentPath));
  };

  const handleProgramUpdate = (index, field, value) => {
    updateContent(`${contentPath}.programs.${index}.${field}`, value);
    setSectionData(getContent(contentPath));
  };

  const handleFeatureUpdate = (programIndex, featureIndex, value) => {
    updateContent(`${contentPath}.programs.${programIndex}.features.${featureIndex}`, value);
    setSectionData(getContent(contentPath));
  };

  const handleAddFeature = (programIndex) => {
    const program = sectionData.programs[programIndex];
    const features = program.features || [];
    updateContent(`${contentPath}.programs.${programIndex}.features`, [...features, '']);
    setSectionData(getContent(contentPath));
  };

  const handleDeleteFeature = (programIndex, featureIndex) => {
    const program = sectionData.programs[programIndex];
    const features = program.features.filter((_, i) => i !== featureIndex);
    updateContent(`${contentPath}.programs.${programIndex}.features`, features);
    setSectionData(getContent(contentPath));
  };

  const handleAddProgram = () => {
    const programs = sectionData?.programs || [];
    const newProgram = {
      id: '',
      title: '',
      description: '',
      icon: 'fas fa-tooth',
      image: '',
      badge: '',
      features: [],
      link: ''
    };

    updateContent(`${contentPath}.programs`, [...programs, newProgram]);
    setSectionData(getContent(contentPath));
  };

  const handleDeleteProgram = (index) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce programme ?')) {
      const programs = sectionData?.programs || [];
      const updatedPrograms = programs.filter((_, i) => i !== index);
      updateContent(`${contentPath}.programs`, updatedPrograms);
      setSectionData(getContent(contentPath));
    }
  };

  if (!sectionData) {
    return (
      <div className="programs-admin-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="programs-admin">
      {/* Section Header Settings */}
      <div className="admin-section-header">
        <div className="section-info">
          <h2>
            <i className="fas fa-star"></i>
            Programmes Phares
          </h2>
          <p>Gérez les programmes de formation mis en avant sur votre site web</p>
        </div>
        <button 
          className="btn-add-program"
          onClick={handleAddProgram}
        >
          <i className="fas fa-plus"></i>
          Ajouter un programme
        </button>
      </div>

      {/* Section Title & Subtitle */}
      <div className="section-settings">
        <div className="setting-group">
          <label>Titre de la section</label>
          <input
            type="text"
            value={sectionData.title || ''}
            onChange={(e) => handleSectionUpdate('title', e.target.value)}
            placeholder="Nos Formations Phares"
          />
        </div>
        <div className="setting-group">
          <label>Sous-titre</label>
          <textarea
            value={sectionData.subtitle || ''}
            onChange={(e) => handleSectionUpdate('subtitle', e.target.value)}
            placeholder="Description des programmes phares..."
            rows={2}
          />
        </div>
      </div>

      {/* Programs List */}
      <div className="programs-list-admin">
        {(sectionData.programs || []).map((program, index) => (
          <div key={index} className="program-item-admin">
            <div className="program-header">
              <div className="program-title-area">
                <h3>
                  <i className="fas fa-graduation-cap"></i>
                  Programme {index + 1}
                </h3>
                {program.badge && (
                  <span className="program-badge">
                    {program.badge}
                  </span>
                )}
              </div>
              <div className="program-actions">
                <button
                  className="btn-delete"
                  onClick={() => handleDeleteProgram(index)}
                  title="Supprimer ce programme"
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            </div>

            {/* Clean Horizontal Form Fields */}
            <div className="form-grid">
              <div className="form-group">
                <label>ID</label>
                <input
                  type="text"
                  value={program.id || ''}
                  onChange={(e) => handleProgramUpdate(index, 'id', e.target.value)}
                  placeholder="cone-beam"
                />
              </div>

              <div className="form-group">
                <label>Titre</label>
                <input
                  type="text"
                  value={program.title || ''}
                  onChange={(e) => handleProgramUpdate(index, 'title', e.target.value)}
                  placeholder="Formation Cone Beam CBCT"
                />
              </div>

              <div className="form-group">
                <label>Icône (optionnel, utilisé si pas d'image)</label>
                <input
                  type="text"
                  value={program.icon || ''}
                  onChange={(e) => handleProgramUpdate(index, 'icon', e.target.value)}
                  placeholder="fas fa-x-ray"
                />
              </div>

              <div className="form-group">
                <label>Badge</label>
                <input
                  type="text"
                  value={program.badge || ''}
                  onChange={(e) => handleProgramUpdate(index, 'badge', e.target.value)}
                  placeholder="Formation Phare"
                />
              </div>

              <div className="form-group full-width">
                <label>Image du Programme</label>
                <ImageUpload
                  value={program.image}
                  onChange={(url) => handleProgramUpdate(index, 'image', url)}
                  label="Télécharger une image"
                />
              </div>

              <div className="form-group full-width">
                <label>Description</label>
                <textarea
                  value={program.description || ''}
                  onChange={(e) => handleProgramUpdate(index, 'description', e.target.value)}
                  placeholder="Formation de référence en imagerie Cone Beam..."
                  rows={3}
                />
              </div>

              <div className="form-group full-width">
                <label>Lien</label>
                <input
                  type="text"
                  value={program.link || ''}
                  onChange={(e) => handleProgramUpdate(index, 'link', e.target.value)}
                  placeholder="/formations#cone-beam"
                />
              </div>

              {/* Features Section */}
              <div className="form-group full-width">
                <div className="features-section">
                  <div className="features-header">
                    <label>Fonctionnalités</label>
                    <button
                      type="button"
                      className="btn-add-feature"
                      onClick={() => handleAddFeature(index)}
                    >
                      <i className="fas fa-plus"></i>
                      Ajouter
                    </button>
                  </div>
                  
                  <div className="features-list">
                    {(program.features || []).map((feature, featureIndex) => (
                      <div key={featureIndex} className="feature-item">
                        <input
                          type="text"
                          value={feature}
                          onChange={(e) => handleFeatureUpdate(index, featureIndex, e.target.value)}
                          placeholder="Fonctionnalité..."
                        />
                        <button
                          type="button"
                          className="btn-delete-feature"
                          onClick={() => handleDeleteFeature(index, featureIndex)}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {(!sectionData.programs || sectionData.programs.length === 0) && (
        <div className="empty-state">
          <div className="empty-icon">
            <i className="fas fa-star"></i>
          </div>
          <h3>Aucun programme phare</h3>
          <p>Commencez par ajouter votre premier programme phare.</p>
          <button className="btn-add-first" onClick={handleAddProgram}>
            <i className="fas fa-plus"></i>
            Ajouter le premier programme
          </button>
        </div>
      )}
    </div>
  );
};

export default FeaturedProgramsAdmin;



